import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jsonStorage, uid } from './persist';

export type TransactionType = 'bet' | 'win' | 'grant' | 'bonus' | 'refund';

export interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  gameId?: string;
  label: string;
  balanceAfter: number;
  createdAt: string;
}

export interface WalletState {
  balance: number;
  currency: 'XCR';
  totalWagered: number;
  totalWon: number;
  transactions: Transaction[];
  /** Monotonic counter used to prevent double-settlement of a round. */
  settledRounds: string[];
  canAfford: (amount: number) => boolean;
  placeBet: (amount: number, gameId?: string, label?: string) => boolean;
  payout: (amount: number, gameId?: string, label?: string, roundId?: string) => boolean;
  grant: (amount: number, label?: string, type?: TransactionType) => void;
  refund: (amount: number, roundId: string, gameId?: string) => boolean;
  reset: (startingBalance?: number) => void;
}

export const STARTING_BALANCE = 10_000;
const MAX_TRANSACTIONS = 500;
const MAX_SETTLED = 2000;

const round2 = (n: number): number => Math.round(n * 100) / 100;

function pushTransaction(
  state: WalletState,
  tx: Omit<Transaction, 'id' | 'createdAt' | 'balanceAfter'>,
  balanceAfter: number,
): Transaction[] {
  const entry: Transaction = {
    ...tx,
    id: uid('tx'),
    balanceAfter,
    createdAt: new Date().toISOString(),
  };
  return [entry, ...state.transactions].slice(0, MAX_TRANSACTIONS);
}

export const useWallet = create<WalletState>()(
  persist(
    (set, get) => ({
      balance: STARTING_BALANCE,
      currency: 'XCR',
      totalWagered: 0,
      totalWon: 0,
      transactions: [],
      settledRounds: [],

      canAfford: (amount) => amount > 0 && get().balance >= amount,

      placeBet: (amount, gameId, label = 'Bet') => {
        const bet = round2(amount);
        const state = get();
        if (bet <= 0 || state.balance < bet) return false;
        const balance = round2(state.balance - bet);
        set({
          balance,
          totalWagered: round2(state.totalWagered + bet),
          transactions: pushTransaction(
            state,
            { type: 'bet', amount: -bet, gameId, label },
            balance,
          ),
        });
        return true;
      },

      payout: (amount, gameId, label = 'Win', roundId) => {
        const win = round2(amount);
        if (win < 0) return false;
        const state = get();
        if (roundId && state.settledRounds.includes(roundId)) return false;
        const balance = round2(state.balance + win);
        set({
          balance,
          totalWon: round2(state.totalWon + win),
          transactions: pushTransaction(
            state,
            { type: 'win', amount: win, gameId, label },
            balance,
          ),
          settledRounds: roundId
            ? [roundId, ...state.settledRounds].slice(0, MAX_SETTLED)
            : state.settledRounds,
        });
        return true;
      },

      grant: (amount, label = 'Bonus credits', type = 'grant') => {
        const value = round2(amount);
        if (value <= 0) return;
        const state = get();
        const balance = round2(state.balance + value);
        set({
          balance,
          transactions: pushTransaction(state, { type, amount: value, label }, balance),
        });
      },

      refund: (amount, roundId, gameId) => {
        const value = round2(amount);
        const state = get();
        if (value <= 0 || state.settledRounds.includes(`refund:${roundId}`)) return false;
        const balance = round2(state.balance + value);
        set({
          balance,
          transactions: pushTransaction(
            state,
            { type: 'refund', amount: value, gameId, label: 'Refund' },
            balance,
          ),
          settledRounds: [`refund:${roundId}`, ...state.settledRounds].slice(0, MAX_SETTLED),
        });
        return true;
      },

      reset: (startingBalance = STARTING_BALANCE) => {
        const balance = round2(startingBalance);
        set({
          balance,
          totalWagered: 0,
          totalWon: 0,
          transactions: [],
          settledRounds: [],
        });
      },
    }),
    {
      name: 'wallet',
      storage: jsonStorage,
      partialize: (state) => ({
        balance: state.balance,
        currency: state.currency,
        totalWagered: state.totalWagered,
        totalWon: state.totalWon,
        transactions: state.transactions.slice(0, 100),
        settledRounds: state.settledRounds,
      }),
    },
  ),
);

/** Net session profit/loss in credits. */
export function netProfit(state: Pick<WalletState, 'totalWon' | 'totalWagered'>): number {
  return Math.round((state.totalWon - state.totalWagered) * 100) / 100;
}
